import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalises a seller URL or domain into Google's public favicon endpoint.
 * Keeping this in one utility prevents seller views from depending on a
 * token-gated logo provider.
 */
export function googleFaviconUrl(
  sellerUrlOrDomain: string | null | undefined,
  size = 64,
): string | null {
  if (!sellerUrlOrDomain) return null;

  try {
    const source = /^https?:\/\//i.test(sellerUrlOrDomain)
      ? sellerUrlOrDomain
      : `https://${sellerUrlOrDomain}`;
    const url = new URL(source);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

    const host = url.hostname.replace(/^www\./i, '');
    if (!host) return null;
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=${size}`;
  } catch {
    return null;
  }
}

const SPACING_MAP: Record<string, string | string[]> = {
  m: 'margin', mt: 'marginTop', mb: 'marginBottom', ml: 'marginLeft', mr: 'marginRight',
  mx: ['marginLeft', 'marginRight'], my: ['marginTop', 'marginBottom'],
  p: 'padding', pt: 'paddingTop', pb: 'paddingBottom', pl: 'paddingLeft', pr: 'paddingRight',
  px: ['paddingLeft', 'paddingRight'], py: ['paddingTop', 'paddingBottom'],
};

const STYLE_PROPS = new Set([
  'fontSize', 'fontWeight', 'lineHeight', 'textAlign', 'color', 'width', 'height',
  'minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'gap', 'rowGap', 'columnGap',
  'position', 'top', 'bottom', 'left', 'right', 'display', 'overflow', 'opacity',
  'flex', 'flexDirection', 'alignItems', 'justifyContent', 'flexWrap', 'flexGrow', 'flexShrink',
  'border', 'borderRadius', 'borderColor', 'borderTop', 'borderBottom',
  'textDecoration', 'whiteSpace', 'textOverflow', 'cursor', 'boxShadow', 'zIndex',
  'background', 'backgroundImage', 'letterSpacing', 'verticalAlign', 'transform',
]);

const DROP_PROPS = new Set([
  'sx', 'component', 'variant', 'gutterBottom', 'ellipsis', 'textTransform',
  'textTransformStyle', 'truncate', 'hoverEffect',
]);

/**
 * Backward-compat bridge: extracts MUI Box props into inline styles.
 * Returns { style, htmlProps } where htmlProps are safe to spread on a DOM element.
 * Temporary — removed in Phase 8 when all callers use Tailwind classes.
 */
export function extractMuiProps(props: Record<string, any>): {
  style: Record<string, any>;
  htmlProps: Record<string, any>;
} {
  const style: Record<string, any> = {};
  const htmlProps: Record<string, any> = {};

  for (const [key, val] of Object.entries(props)) {
    if (key === 'style' || key === 'children' || key === 'className' ||
        key === 'dangerouslySetInnerHTML' || key === 'ref') continue;

    if (key in SPACING_MAP) {
      const px = typeof val === 'number' ? `${val * 8}px` : val;
      const target = SPACING_MAP[key];
      if (Array.isArray(target)) {
        target.forEach(t => { style[t] = px; });
      } else {
        style[target] = px;
      }
    } else if (key === 'bgcolor') {
      style.backgroundColor = val;
    } else if (STYLE_PROPS.has(key)) {
      style[key] = val;
    } else if (key === 'sx' && typeof val === 'object' && val !== null) {
      for (const [sk, sv] of Object.entries(val)) {
        if (typeof sv === 'function') continue;
        if (sk in SPACING_MAP) {
          const px = typeof sv === 'number' ? `${(sv as number) * 8}px` : sv;
          const target = SPACING_MAP[sk];
          if (Array.isArray(target)) {
            target.forEach(t => { style[t] = px as string; });
          } else {
            style[target] = px as string;
          }
        } else if (sk === 'bgcolor') {
          style.backgroundColor = sv;
        } else if (STYLE_PROPS.has(sk)) {
          style[sk] = sv;
        }
      }
    } else if (!DROP_PROPS.has(key)) {
      htmlProps[key] = val;
    }
  }

  return { style, htmlProps };
}
